import { useEffect, useState } from 'react';
import { authenticatedFetch } from '../../../common/api';
import {
  closestMultiples,
  createCart,
  getCreateOrderUrl,
  isValueMultipleOfIncrement,
} from '../utils';
import {
  FETCH_APP_ORDER_API,
  FETCH_SEARCH_PRODUCTS_API,
} from '../../../features/csvUpload/constants';
import { addAttributesToCart, formattedIDs, getInlineError, useDebounce } from '../../utils';
import { getURLWithBasicParams } from '../request';
import { getURLWithParamsFromArray } from '../../../common/util';
import { getCountry } from '../../../common/pageData';
const maxQuantity = 99999;

export interface singleProduct {
  variantId: string;
  variantName: string;
  productName: string;
  image?: string;
  min?: number;
  max?: number;
  availableStock?: number;
  increment?: number;
  quantity?: number;
  error?: string;
}

const defaultSingleProduct: singleProduct = {
  variantId: '',
  variantName: '',
  productName: '',
  image: undefined,
  min: undefined,
  max: undefined,
  availableStock: undefined,
  increment: undefined,
  quantity: undefined,
  error: undefined,
};

const a = [
  {
    image: '',
    variantName: '7 / Blue',
    min: 7,
    max: 1995,
    quantity: 7,
    increment: 7,
    variantId: '46449736843509',
    productName: 'Copy of Flip Flops - Blue',
    sku: '7-blue-sku',
    barcode: '7-blue-barcode',
    mrOrderId: 'MRCA20250706203357_prcoP',
    availableStock: 25,
  },
  {
    image: '',
    variantName: '8 / Blue',
    min: 7,
    max: 1995,
    quantity: 7,
    increment: 7,
    variantId: '46449736876277',
    productName: 'Copy of Flip Flops - Blue',
    sku: '8-blue-sku',
    barcode: '8-blue-barcode',
    mrOrderId: 'MRCA20250706203357_prcoP',
    availableStock: 99999,
  },
];

const columnsTokens = {
    variant_id: 'variantId',
    quantity: 'quantity',
}

export function useQuickSearchState(
  offerdata,
  selectedLocation,
  columns,
  isGoToCheckout
) {
  const [searchedValue, setSearchedValue] = useState('');
  const [searchedProducts, setSearchedProducts] = useState([]);
  const [quickSearchSelectedProduct, setQuickSearchSelectedProduct] =
    useState<singleProduct>(defaultSingleProduct);

  const [quickSearchedTableData, setQuickSearchedTableData] = useState<
    singleProduct[]
  >([]);
  const [mrOrderId, setMrOrderId] = useState(null);
  const [orderUrl, setOrderUrl] = useState('');
  const [createOrderButtonLoading, setCreateOrderButtonLoading] =
    useState(false);
  const [searchedProductsLoading, setSearchedProductsLoading] = useState(false);

  useEffect(() => {
    setOrderUrl('');
    if (quickSearchedTableData.length === 0) {
      setMrOrderId(null);
    }
  }, [quickSearchedTableData]);

  const commonFunctionForValidation = (sp: singleProduct, value: number) => {
    sp.error = undefined;

    const min = sp.min || 1;
    const max = Math.min(
      sp.max || maxQuantity,
      sp.availableStock || maxQuantity,
    );
    const availableStock = sp.availableStock || maxQuantity;
    const increment = sp.increment || 1;

    const maxQtyThatCanBeAdded = Math.min(
      max,
      availableStock,
      closestMultiples(increment, max)[0],
    );
    const minQtyToBeAdded = Math.max(0, Math.min(min, availableStock));

    sp.quantity = value;
    if (value > maxQtyThatCanBeAdded) {
      const error = getInlineError({
        error: 'errorMaxOrderQuantityText',
        incrementValue: increment,
        themeProps: offerdata.theme,
      });
      sp.error = `${error} (${maxQtyThatCanBeAdded})`;
    } else if (value < minQtyToBeAdded) {
      const error = getInlineError({
        error: 'errorMinOrderQuantityText',
        incrementValue: increment,
        themeProps: offerdata.theme,
      });
      sp.error = `${error} (${minQtyToBeAdded})`;
    } else {
      const isChangedValuedMultipleOfIncrement = isValueMultipleOfIncrement(
        value,
        increment,
      );

      if (!isChangedValuedMultipleOfIncrement) {
        // const [lowerClosest, higherClosest] = closestMultiples(
        //     increment,
        //     value,
        // );
        // const nearestValue =
        //     value - lowerClosest <= higherClosest - value
        //         ? lowerClosest
        //         : higherClosest;
        const error = getInlineError({
          error: 'errorIncrementQuantityText',
          incrementValue: increment,
          themeProps: offerdata.theme,
        });
        sp.error = error;
      }
    }

    return sp;
  };

  const onQuickSearchProductChangeForSearchRow = (value) => {
    const changedProduct = structuredClone(quickSearchSelectedProduct);

    const validatedProduct = commonFunctionForValidation(changedProduct, value);

    setQuickSearchSelectedProduct(validatedProduct);
  };

  const onQuickSearchProductAdd = (d: singleProduct) => {
    setQuickSearchedTableData((prev) => {
      const existingProductIndex = prev.findIndex(
        (product) => product.variantId === d.variantId,
      );

      const min = d.min || 1;
      const max = Math.min(
        d.max || maxQuantity,
        d.availableStock || maxQuantity,
      );
      const availableStock = d.availableStock || maxQuantity;
      const increment = d.increment || 1;

      const maxQtyThatCanBeAdded = Math.min(
        max,
        availableStock,
        closestMultiples(increment, max)[0],
      );
      const minQtyToBeAdded = Math.min(
        min,
        availableStock,
        closestMultiples(increment, min)[0],
        0,
      );

      if (existingProductIndex !== -1) {
        const updatedProduct = { ...prev[existingProductIndex] };
        let newQuantity = (updatedProduct.quantity || 0) + (d.quantity || 0);

        if (newQuantity > maxQtyThatCanBeAdded) {
          newQuantity = maxQtyThatCanBeAdded;
        } else if (newQuantity < minQtyToBeAdded) {
          newQuantity = minQtyToBeAdded;
        } else {
          const isNewQuantityMultipleOfIncrement = isValueMultipleOfIncrement(
            newQuantity,
            increment,
          );

          if (!isNewQuantityMultipleOfIncrement) {
            const [lowerClosest, higherClosest] = closestMultiples(
              increment,
              newQuantity,
            );
            newQuantity =
              newQuantity - lowerClosest <= higherClosest - newQuantity
                ? lowerClosest
                : higherClosest;
          }
        }

        updatedProduct.quantity = newQuantity;
        updatedProduct.error = '';
        const newData = [...prev];
        newData[existingProductIndex] = updatedProduct;
        return newData;
      }

      let initialQuantity = d.quantity || min;

      if (initialQuantity > maxQtyThatCanBeAdded) {
        initialQuantity = maxQtyThatCanBeAdded;
      } else if (initialQuantity < minQtyToBeAdded) {
        initialQuantity = minQtyToBeAdded;
      } else {
        const isInitialQuantityMultipleOfIncrement = isValueMultipleOfIncrement(
          initialQuantity,
          increment,
        );

        if (!isInitialQuantityMultipleOfIncrement) {
          const [lowerClosest, higherClosest] = closestMultiples(
            increment,
            initialQuantity,
          );
          initialQuantity =
            initialQuantity - lowerClosest <= higherClosest - initialQuantity
              ? lowerClosest
              : higherClosest;
        }
      }

      return [...prev, { ...d, quantity: initialQuantity }];
    });
  };

  const onQuickSearchProductChange = (variantId, index, value) => {
    const existingProductIndex = quickSearchedTableData.findIndex(
      (product) => product.variantId === variantId,
    );

    if (existingProductIndex === -1) {
      console.error(`Product with variantId ${variantId} does not exist.`);
      return;
    }

    const changedProduct = structuredClone(
      quickSearchedTableData[existingProductIndex],
    );

    const validatedProduct = commonFunctionForValidation(changedProduct, value);

    setQuickSearchedTableData((prev) => {
      const newData = [...prev];
      newData[existingProductIndex] = validatedProduct;
      return newData;
    });
  };

  const onClickCreateOrder = async () => {
    if (!orderUrl) {
        setCreateOrderButtonLoading(true);


      const requestBody = {
        locationID: selectedLocation?.id || null,
        countryCode: getCountry(selectedLocation?.countryCode),
        mrOrderID: mrOrderId,
        source: 'quick_search',
      };
      // we redirect the user to cart or checkout based on the extension setup where they can purchase the items
      const data = await authenticatedFetch({
        api: FETCH_APP_ORDER_API,
        method: 'POST',
        isRequestBodyPresent: true,
        requestBody,
        useBasicParams: true,
      });

      if (data) {
        const shopURL = offerdata.shopURL;
        const orderUrl = getCreateOrderUrl(
          shopURL,
          quickSearchedTableData,
          columnsTokens,
          mrOrderId,
          isGoToCheckout,
        );

        if (orderUrl.length < 2000) {
          window.location.href = orderUrl;
          setCreateOrderButtonLoading(false);
        } else {
            const data = await createCart(
              quickSearchedTableData,
              columnsTokens,
              columns,
            );
            await addAttributesToCart(mrOrderId);
            let redirectPath = isGoToCheckout ? '/checkout' : '/cart';
            const toastParam = data ? `?toast=${data}` : '';
            window.location.href = `${redirectPath}${toastParam}`;
        }
      }
    }
  };

  const fetchSearchedProducts = useDebounce(async (searchValue: string) => {
    const params = [
      { key: 'identifier', value: searchValue },
      {
        key: 'locationID',
        value: selectedLocation?.id?.toString(),
      },
      // {
      //   key: 'companyID',
      //   value: formattedIDs('2046984437', 'Company'),
      // },
      { key: 'countryCode', value: selectedLocation?.countryCode || 'US' },
    ];

    if (mrOrderId) {
      params.push({ key: 'mrOrderID', value: mrOrderId });
    }

    setSearchedProducts([]);
    setSearchedProductsLoading(true);

    console.log('Fetching searched products with params:', params);

    const d = await authenticatedFetch({
      api: `${getURLWithParamsFromArray(`${getURLWithBasicParams(`${FETCH_SEARCH_PRODUCTS_API}?`, false)}&`, params, false)}`,
    });

    console.log('Fetched searched products:', d);
    if (d) {
      if (d[0]?.mrOrderId) {
        setMrOrderId(d[0].mrOrderId);
      }
      setSearchedProducts(d);

      const matchedProduct = d.find(
        (product) =>
          product.sku?.toLowerCase() === searchValue.toLowerCase() ||
          product.barcode?.toLowerCase() === searchValue.toLowerCase() ||
          product.variantName?.toLowerCase() === searchValue.toLowerCase() ||
          product.productName?.toLowerCase() + '~'  + product.variantName?.toLowerCase() === searchValue.toLowerCase(),
      );

      if (matchedProduct) {
        setQuickSearchSelectedProduct((prev) => {
          const q = prev.quantity || matchedProduct.quantity;
          const s = commonFunctionForValidation(matchedProduct, q);
          return s;
        });
      }
    }
    setSearchedProductsLoading(false);
  }, 300);

  return {
    searchedValue,
    setSearchedValue,
    searchedProducts,
    setSearchedProducts,
    quickSearchedTableData,
    setQuickSearchedTableData,
    quickSearchSelectedProduct,
    setQuickSearchSelectedProduct,
    // mrOrderId,
    // setMrOrderId,
    orderUrl,
    // setOrderUrl,
    createOrderButtonLoading,
    setCreateOrderButtonLoading,
    searchedProductsLoading,
    // setSearchedProductsLoading,

    // utlities
    fetchSearchedProducts,
    onQuickSearchProductAdd,
    onQuickSearchProductChange,
    onClickCreateOrder,
    // commonFunctionForValidation,
    onQuickSearchProductChangeForSearchRow,
  };
}
